import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateInWords } from 'utils/formatDate';
import {
  analizarApelacion,
  generarEscritoApelacion,
  recalcularApelacion,
} from 'services/escritosJudicialesService';
import SubidaDocumentos from './SubidaDocumentos';

const CLAVE_AGRAVIO = {
  omision_pronunciamiento: 'cw_ape_ag_omisionPronunciamiento',
  omision_ponderacion_prueba: 'cw_ape_ag_omisionPonderacion',
  interpretacion_divergente: 'cw_ape_ag_interpretacion',
  apreciacion_hechos: 'cw_ape_ag_hechos',
  fundamentacion_aparente: 'cw_ape_ag_fundamentacion',
  contradiccion: 'cw_ape_ag_contradiccion',
  alteracion_carga_prueba: 'cw_ape_ag_cargaPrueba',
  ultra_petita: 'cw_ape_ag_ultraPetita',
  otro: 'cw_ape_ag_otro',
};

/**
 * Apelación de la sentencia que rechazó las excepciones. ~161 al mes.
 *
 * El plazo para apelar es FATAL y se cuenta en días hábiles desde la notificación de la sentencia
 * —no desde su fecha—. Ese cómputo lo hace el backend, con el sábado hábil.
 */
const ApelacionPanel = ({ DesignSystemComponents, caseData, t }) => {
  const { Badge, Button, Checkbox, Input, Loader, Alert } = DesignSystemComponents || {};

  const [documentos, setDocumentos] = useState([]);
  const [analisis, setAnalisis] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);

  // Qué excepciones rechazadas se apelan. Se preseleccionan todas: si el análisis las encontró
  // rechazadas y con agravio, el recurso normalmente las lleva todas.
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [abogado, setAbogado] = useState('');
  const [escrito, setEscrito] = useState(null);
  const [generando, setGenerando] = useState(false);

  const abortAnalisis = useRef(null);
  const abortRecalculo = useRef(null);
  const abortGenerar = useRef(null);

  useEffect(
    () => () => {
      abortAnalisis.current?.abort();
      abortRecalculo.current?.abort();
      abortGenerar.current?.abort();
    },
    []
  );

  const analizar = useCallback(async () => {
    if (!documentos.length) return;
    abortAnalisis.current?.abort();
    const controller = new AbortController();
    abortAnalisis.current = controller;

    setCargando(true);
    setError(null);
    try {
      const data = await analizarApelacion(
        documentos,
        caseData?.id,
        controller.signal
      );
      setAnalisis(data);
      setEscrito(null);
      setSeleccionadas((data.excepcionesRechazadas || []).map((e) => e.id));
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.response?.data?.message || err.message);
    } finally {
      setCargando(false);
    }
  }, [documentos, caseData?.id]);

  const alternar = useCallback((id) => {
    setSeleccionadas((previo) =>
      previo.includes(id) ? previo.filter((x) => x !== id) : [...previo, id]
    );
  }, []);

  const descargar = useCallback((documento) => {
    if (!documento?.archivoBase64) return;
    // atob devuelve una cadena de bytes: hay que pasarla a Uint8Array o el .docx llega corrupto.
    const bytes = Uint8Array.from(atob(documento.archivoBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = documento.nombreDeArchivo || 'apelacion.docx';
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
  }, []);

  const generar = useCallback(async () => {
    if (!analisis || seleccionadas.length === 0) return;
    abortGenerar.current?.abort();
    const controller = new AbortController();
    abortGenerar.current = controller;

    setGenerando(true);
    setError(null);
    try {
      const data = await generarEscritoApelacion(
        analisis,
        seleccionadas,
        abogado.trim() || null,
        caseData?.id,
        controller.signal
      );
      setEscrito(data);
      descargar(data);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.response?.data?.message || err.message);
    } finally {
      setGenerando(false);
    }
  }, [analisis, seleccionadas, abogado, caseData?.id, descargar]);

  /**
   * Corrige un antecedente y recalcula. `campo` es 'fechaNotificacion', 'hayAgravio' o
   * 'esSentenciaDefinitiva'.
   *
   * Los dos últimos son juicios de admisibilidad que hace el abogado, y sin poder fijarlos una
   * corrida donde el modelo dude queda bloqueada: el generador exige agravio acreditado.
   */
  const corregir = useCallback((campo, valor) => {
    setAnalisis((previo) => {
      if (!previo) return previo;
      const siguiente = structuredClone(previo);

      if (campo === 'fechaNotificacion') siguiente.causa.fechaNotificacion = valor || null;
      else siguiente.admisibilidad[campo] = valor;

      abortRecalculo.current?.abort();
      const controller = new AbortController();
      abortRecalculo.current = controller;

      recalcularApelacion(siguiente, controller.signal)
        .then((data) => setAnalisis(data))
        .catch((err) => {
          if (err.name === 'AbortError' || err.name === 'CanceledError') return;
          setError(err.customMessage || err.message);
        });

      return siguiente;
    });
  }, []);

  const admisibilidad = analisis?.admisibilidad;
  const dias = admisibilidad?.diasHabilesRestantes;

  return (
    <div className='d-flex flex-column gap-4'>
      <SubidaDocumentos
        DesignSystemComponents={DesignSystemComponents}
        titulo={t('cw_ape_adjunta')}
        ayuda={t('cw_ape_adjunta_hint')}
        archivos={documentos}
        onArchivos={setDocumentos}
        onAnalizar={analizar}
        cargando={cargando}
        error={error}
        etiquetaBoton={t('cw_ape_analizar')}
        etiquetaCargando={t('cw_analyzing')}
        t={t}
      />

      {cargando && <Loader />}

      {analisis && (
        <>
          <div>
            <div className='fw-semibold mb-2'>{t('cw_ape_admisibilidad')}</div>
            <div className='row g-3'>
              <div className='col-6 col-md-3'>
                <div className='color-gray-600 small'>{t('cw_ape_vence')}</div>
                <div className='fw-bold fs-5'>
                  {formatDateInWords(admisibilidad?.vencimientoPlazo)}
                </div>
              </div>
              <div className='col-6 col-md-3'>
                <div className='color-gray-600 small'>{t('cw_ape_diasHabiles')}</div>
                <div className='fw-bold fs-5'>
                  {dias === null || dias === undefined ? '—' : dias}
                </div>
              </div>
              <div className='col-6 col-md-3'>
                <div className='color-gray-600 small'>{t('cw_ape_sentenciaDefinitiva')}</div>
                <div>{admisibilidad?.esSentenciaDefinitiva || '—'}</div>
              </div>
              <div className='col-6 col-md-3'>
                <div className='color-gray-600 small'>{t('cw_ape_hayAgravio')}</div>
                <div>{admisibilidad?.hayAgravio || '—'}</div>
              </div>
            </div>

            {admisibilidad?.enRiesgo && (
              <Alert className='alerta-en-linea' variant='danger'>
                {t('cw_ape_enRiesgo')}
              </Alert>
            )}
          </div>

          <div>
            <div className='small mb-1'>{t('cw_ape_fechaNotificacion')}</div>
            <input
              type='date'
              className='form-control'
              value={analisis.causa?.fechaNotificacion || ''}
              onChange={(e) => corregir('fechaNotificacion', e.target.value)}
            />
            <div className='color-gray-600 small mt-1'>
              {t('cw_ape_fechaNotificacion_hint')}
            </div>
          </div>

          {/* Los dos juicios de admisibilidad. Los propone el análisis y los decide el abogado:
              sin agravio no hay recurso, y eso es requisito legal, no una opinión del modelo. */}
          <div className='row g-3'>
            <div className='col-12 col-md-6'>
              <div className='small mb-1'>{t('cw_ape_hayAgravio')}</div>
              <select
                className='form-select'
                value={analisis.admisibilidad?.hayAgravio || 'indeterminado'}
                onChange={(e) => corregir('hayAgravio', e.target.value)}
              >
                <option value='si'>{t('cw_ape_agravio_si')}</option>
                <option value='no'>{t('cw_ape_agravio_no')}</option>
                <option value='indeterminado'>{t('cw_ape_agravio_indeterminado')}</option>
              </select>
              <div className='color-gray-600 small mt-1'>{t('cw_ape_agravio_hint')}</div>
            </div>

            <div className='col-12 col-md-6'>
              <div className='small mb-1'>{t('cw_ape_sentenciaDefinitiva')}</div>
              <select
                className='form-select'
                value={analisis.admisibilidad?.esSentenciaDefinitiva || 'indeterminado'}
                onChange={(e) => corregir('esSentenciaDefinitiva', e.target.value)}
              >
                <option value='si'>{t('cw_ape_definitiva_si')}</option>
                <option value='no'>{t('cw_ape_definitiva_no')}</option>
                <option value='indeterminado'>{t('cw_ape_definitiva_indeterminado')}</option>
              </select>
            </div>
          </div>

          <div>
            <div className='fw-semibold mb-2'>
              {t('cw_ape_rechazadas', { count: analisis.excepcionesRechazadas?.length || 0 })}
            </div>

            {(analisis.excepcionesRechazadas || []).map((excepcion) => (
              <div key={excepcion.id} className='border-top pt-3 mb-3'>
                <div className='d-flex align-items-center gap-2 mb-1'>
                  {Checkbox && (
                    <Checkbox
                      id={`apelacion-${excepcion.id}`}
                      label=''
                      checked={seleccionadas.includes(excepcion.id)}
                      onChange={() => alternar(excepcion.id)}
                    />
                  )}
                  <span className='fw-bold'>{excepcion.numeral}</span>
                  <span>{excepcion.denominacionLiteral}</span>
                  {excepcion.omisionPronunciamiento && (
                    <Badge text={t('cw_ape_noSePronuncio')} color='danger' size='sm' />
                  )}
                </div>

                {excepcion.considerandos && (
                  <div className='color-gray-600 small mb-2'>
                    {excepcion.considerandos}
                  </div>
                )}

                <div className='mb-2'>{excepcion.comoLaResolvio}</div>

                <div className='color-gray-600 small mb-1'>
                  {t('cw_ape_agravios', { count: excepcion.agravios?.length || 0 })}
                </div>
                <ul>
                  {(excepcion.agravios || []).map((agravio, i) => (
                    <li key={i} className='mb-2'>
                      <span className='fw-semibold'>
                        {CLAVE_AGRAVIO[agravio.tipo] ? t(CLAVE_AGRAVIO[agravio.tipo]) : agravio.tipo}
                      </span>
                      {agravio.ubicacion && (
                        <span className='color-gray-600 small'> · {agravio.ubicacion}</span>
                      )}
                      {agravio.norma && (
                        <span className='color-gray-600 small'> · {agravio.norma}</span>
                      )}
                      <div>{agravio.fundamento}</div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className='border-top pt-3'>
            <p className='font-light mb-2'>{t('cw_ape_generarTitulo')}</p>
            <p className='caption color-gray-600'>{t('cw_ape_generar_hint')}</p>

            {Input && (
              <div className='row g-3 mb-3'>
                <div className='col-12 col-md-6'>
                  <Input
                    label={t('cw_ape_abogado')}
                    value={abogado}
                    onChange={(e) => setAbogado(e.target.value)}
                    helperText={t('cw_ape_abogado_hint')}
                  />
                </div>
              </div>
            )}

            <div className='d-flex align-items-center gap-3'>
              <Button
                label={
                  generando
                    ? t('cw_ape_redactando')
                    : t('cw_ape_generar', { count: seleccionadas.length })
                }
                size='sm'
                variant='primary'
                onClick={generar}
                disabled={seleccionadas.length === 0 || generando}
              />
              {escrito && (
                <>
                  <Button
                    label={t('cw_writ_download')}
                    size='sm'
                    variant='naked'
                    onClick={() => descargar(escrito)}
                  />
                  <span className='caption color-gray-600'>
                    {t('cw_ape_palabras', {
                      palabras: escrito.palabras,
                      minimo: escrito.minimoSugerido,
                    })}
                  </span>
                </>
              )}
            </div>

            {escrito?.advertencias?.length > 0 && (
              <div className='mt-3'>
                <div className='fw-semibold mb-2'>{t('cw_ape_revisar')}</div>
                {escrito.advertencias.map((a, i) => (
                  <Alert className='alerta-en-linea' key={i} variant='warning'>
                    {a}
                  </Alert>
                ))}
              </div>
            )}
          </div>

          {analisis.advertencias?.length > 0 && (
            <div>
              <div className='fw-semibold mb-2'>{t('cw_warnings')}</div>
              {analisis.advertencias.map((a, i) => (
                <Alert className='alerta-en-linea' key={i} variant={a.startsWith('⛔') ? 'danger' : 'warning'}>
                  {a}
                </Alert>
              ))}
            </div>
          )}

          {analisis.datosFaltantes?.length > 0 && (
            <div>
              <div className='fw-semibold mb-2'>{t('cw_missing')}</div>
              <ul className='mb-0'>
                {analisis.datosFaltantes.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ApelacionPanel;
